import React from 'react';
import { ChevronDownIcon } from '@/components/Icons';
import { TSelectOptions } from '@/components/Select';
import { TWalletData } from '@/features/home/types/WalletData';
import { ALL_ACCOUNTS } from '@/context/HomeProvider/HomeContextProvider';
import useHomeContext from '@/hooks/useHomeContext';
import { useNavigate, generatePath } from 'react-router-dom';
import { routes } from '@/utils/routes';
import { isFeatureEnabled } from '@/config/featureFlags.service';
import { Feature } from '@/config/featureFlags.definition';

interface AccountMenuProps {
  showNavBarMenu?: string;
  onOpenNavigation: (menu: string) => void;
  onCloseMenu: () => void;
  allWallets: TWalletData[];
  allOptions: TSelectOptions[];
}

export const AccountMenu: React.FC<AccountMenuProps> = ({
  showNavBarMenu,
  onOpenNavigation: handleNavBarMenuDropdown,
  onCloseMenu,
  allWallets,
  allOptions
}) => {
  const { setSelectedAccount, setBankAccount, allAccounts, defaultOption, setAccountsFilter } =
    useHomeContext()!;
  const navigate = useNavigate();

  const handleSelectAccount = (option: TSelectOptions) => {
    if (option.label === ALL_ACCOUNTS) {
      setBankAccount(allAccounts);
      setSelectedAccount(defaultOption);
      setAccountsFilter([]);
    } else {
      const found = allWallets.find((w) => w.name === option.value)!;
      setSelectedAccount({
        label: found.name,
        labelDescription: found.balance!.toString(),
        value: found.name
      });
      setBankAccount(found);
      setAccountsFilter([found.id]);
      onCloseMenu();
      if (isFeatureEnabled(Feature.SELECTED_ACCOUNT)) {
        navigate(generatePath(routes.account, { walletId: found.id }));
      } else if (window.location.pathname !== routes.home) {
        navigate(routes.home);
      }
      return;
    }
    onCloseMenu();
    if (window.location.pathname !== routes.home) {
      navigate(routes.home);
    }
  };

  return (
    <div
      className="flex text-xs leading-6 gap-x-[5px] items-center relative cursor-pointer"
      onClick={() => handleNavBarMenuDropdown('accounts')}>
      Accounts
      <ChevronDownIcon size="xxs" className={showNavBarMenu === 'accounts' ? 'rotate-180' : ''} />
      {showNavBarMenu === 'accounts' ? (
        <div
          className="absolute top-6 bg-white text-emerge-navy z-50 rounded-[6px] -left-4 overflow-y-auto vertical-scroll w-[224px] max-h-[240px] shadow-sm"
          onMouseLeave={onCloseMenu}>
          {allOptions.map((option, idx) => {
            const isAllAccounts = option.label === ALL_ACCOUNTS;
            return (
              <React.Fragment key={`account-menu-option-${idx}`}>
                {/* "View all accounts" row */}
                {isAllAccounts ? (
                  <>
                    <div
                      className="px-4 py-3 bg-transparent hover:bg-emerge-base-2"
                      onClick={() => handleSelectAccount(option)}>
                      <span className="text-emerge-navy text-xs font-medium">
                        View all accounts
                      </span>
                    </div>
                    {/* Divider beneath "View all accounts" */}
                    <div className="h-px bg-aux-gray" />
                  </>
                ) : (
                  /* Individual account row — name + balance stacked */
                  <div
                    className="py-0.5 bg-transparent hover:bg-emerge-base-2"
                    onClick={() => handleSelectAccount(option)}>
                    <div className="px-4 py-1">
                      <div className="text-emerge-navy text-xs font-medium leading-[1.2]">
                        {option.label}
                      </div>
                      <div className="text-emerge-navy-2 text-[10px] font-medium leading-[1.2]">
                        ${Number(option.labelDescription).toFixed(2)}
                      </div>
                    </div>
                  </div>
                )}
              </React.Fragment>
            );
          })}
        </div>
      ) : null}
    </div>
  );
};
